// SUN-180 (SUN-174 lane C): vendor <-> product links by Excel.
// "Preview first, then Save — same as the BOM upload. Unknown vendor or product → shown in red, never created by accident."
import { useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { api } from '@/utils/api';
import { useAuthStore } from '@/stores/auth';
import { isMasterdataApprover } from '@/lib/masterdataApprovers';

interface PlanLine {
  row: number; vendorText: string; productText: string;
  vendor?: { id: string; name: string };
  product?: { id: string; sku: string | null; name: string };
  input?: { vendorId: string; productId: string; vendorItemCode?: string | null; rate?: number | null; unit?: string | null; leadTimeDays?: number | null; isPreferred?: boolean };
  error?: string; matchedBy?: 'sku' | 'barcode' | 'name'; vendorMatchedBy?: 'name' | 'gstin'; action?: 'create' | 'update';
}
export interface VendorProductsPlan {
  ok: boolean; lines: PlanLine[];
  counts: { lines: number; errors: number; create: number; update: number };
  notes: string[];
}

// The shared client sends JSON by default, which turns a FormData body into JSON with an empty file.
const MULTIPART = { headers: { 'Content-Type': 'multipart/form-data' } };
const apiError = (e: any, fallback: string): string => e?.response?.data?.error || e?.response?.data?.message || fallback;
const dash = (v: unknown) => (v === null || v === undefined || v === '' ? '—' : String(v));

/** Who sees the buttons: the masterdata approvers (mirror of raw-material-approval.ts). The server decides anyway. */
export const canEditVendorProducts = (user: any) => isMasterdataApprover(user);

export function VendorProductsExcelButtons({ onSaved }: { onSaved: () => void }) {
  const { user } = useAuthStore();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  if (!canEditVendorProducts(user)) return null;

  const download = async () => {
    setError(null); setNotice(null);
    try {
      const res = await api.get('/vendors/products/import-template', { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = 'vendor-products-format.xlsx'; a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) { setError(apiError(e, 'Could not download the format.')); }
  };

  return (
    <>
      <button type="button" onClick={download} className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">⬇ Download format</button>
      <button type="button" onClick={() => { setNotice(null); setOpen(true); }} className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">⬆ Upload Excel</button>
      {error && <span role="alert" className="text-xs text-red-700">{error}</span>}
      {notice && <span role="status" className="text-xs text-green-700">{notice}</span>}
      {open && <VendorProductsImportModal onClose={() => setOpen(false)} onSaved={(msg) => { setNotice(msg); setOpen(false); onSaved(); }} />}
    </>
  );
}

/** Upload a sheet, check the preview, then save. Nothing is written until "Save". onSaved gets "Saved: X new, Y updated". */
export function VendorProductsImportModal({ onClose, onSaved }: { onClose: () => void; onSaved: (message: string) => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [plan, setPlan] = useState<VendorProductsPlan | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const form = (f: File) => { const fd = new FormData(); fd.append('file', f); return fd; };

  const pick = async (f: File | null) => {
    setFile(f); setPlan(null); setError(null);
    if (!f) return;
    setBusy(true);
    try { setPlan((await api.post('/vendors/products/import/preview', form(f), MULTIPART)).data.data); }
    catch (e: any) { setError(apiError(e, 'Could not read the file.')); }
    finally { setBusy(false); }
  };

  const save = async () => {
    if (!file || !plan?.ok) return;
    setBusy(true); setError(null);
    try {
      const out = (await api.post('/vendors/products/import', form(file), MULTIPART)).data.data;
      onSaved(`Saved: ${out.created} new, ${out.updated} updated`);
    } catch (e: any) { setError(apiError(e, 'Could not save the sheet.')); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" role="dialog" aria-label="Upload vendor products from Excel">
      <div className="max-h-[90vh] w-full max-w-5xl overflow-y-auto rounded-xl bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Upload vendor products from Excel</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="rounded p-1 text-gray-400 hover:bg-gray-100"><X className="h-5 w-5" /></button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">Sheet (.xlsx or .csv)</label>
            <input type="file" accept=".xlsx,.csv" aria-label="Vendor products sheet" onChange={(e) => { void pick(e.target.files?.[0] ?? null); }} className="block w-full text-sm" />
          </div>
          {busy && !plan && <p className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="h-4 w-4 animate-spin" />Reading the sheet…</p>}
          {error && <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

          {plan && (
            <div className="space-y-3">
              <p className="text-sm text-gray-700" data-testid="vendor-products-summary">
                {plan.counts.lines} lines · {plan.counts.create} new · {plan.counts.update} update · {plan.counts.errors} errors
              </p>
              {plan.notes.length > 0 && (
                <ul className="list-disc space-y-0.5 pl-5 text-xs text-amber-800">{plan.notes.map((n) => <li key={n}>{n}</li>)}</ul>
              )}
              <table className="w-full text-sm">
                <thead><tr className="border-b border-gray-200 bg-gray-50 text-left text-xs text-gray-500">
                  <th className="px-3 py-2">Row</th><th className="px-3 py-2">Vendor</th><th className="px-3 py-2">Product</th><th className="px-3 py-2">Vendor&apos;s code</th>
                  <th className="px-3 py-2 text-right">Rate</th><th className="px-3 py-2">Unit</th><th className="px-3 py-2">Lead time</th><th className="px-3 py-2">Preferred</th><th className="px-3 py-2" />
                </tr></thead>
                <tbody className="divide-y divide-gray-100">
                  {plan.lines.map((l) => (
                    <tr key={l.row} className={l.error ? 'bg-red-50 text-red-700' : ''} data-testid={`plan-row-${l.row}`}>
                      <td className="px-3 py-2">{l.row}</td>
                      <td className="px-3 py-2">{l.vendor?.name ?? l.vendorText}</td>
                      <td className="px-3 py-2">
                        <p>{l.product?.name ?? l.productText}</p>
                        {l.product?.sku && <p className="font-mono text-xs text-gray-500">{l.product.sku}</p>}
                        {l.error && <p className="text-xs font-medium text-red-700">{l.error}</p>}
                      </td>
                      <td className="px-3 py-2">{dash(l.input?.vendorItemCode)}</td>
                      <td className="px-3 py-2 text-right">{dash(l.input?.rate)}</td>
                      <td className="px-3 py-2">{dash(l.input?.unit)}</td>
                      <td className="px-3 py-2">{l.input?.leadTimeDays == null ? '—' : `${l.input.leadTimeDays} ${l.input.leadTimeDays === 1 ? 'day' : 'days'}`}</td>
                      <td className="px-3 py-2">{l.input?.isPreferred ? '★' : ''}</td>
                      <td className="px-3 py-2 text-xs">{l.error ? '' : l.action === 'create' ? 'New' : l.action === 'update' ? 'Update' : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!plan.ok && <p className="text-sm text-red-700">Fix the red lines in the sheet and upload again.</p>}
            </div>
          )}

          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">Cancel</button>
            {plan && (
              <button type="button" onClick={save} disabled={!plan.ok || busy}
                className="inline-flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />}Save
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
